import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Copy, Download, Linkedin } from "lucide-react";
import { KIT_IMAGES, KIT_POSTS, renderKitImage } from "@/lib/ambassadorKit";

const KitImage = ({ design, amb }) => {
  const ref = useRef(null);
  const [ready, setReady] = useState(false);
  useEffect(() => { renderKitImage(ref.current, design, amb).then(() => setReady(true)).catch(() => {}); }, [design, amb]);
  const download = () => Object.assign(document.createElement("a"), { href: ref.current.toDataURL("image/png"), download: `CSI-${design.id}-${amb.code}.png` }).click();
  return (
    <div className="card-warm p-3" data-testid={`kit-image-${design.id}`}>
      <canvas ref={ref} className="w-full h-auto rounded-lg bg-[var(--muted-surface)]" style={{ aspectRatio: `${design.w}/${design.h}` }}/>
      <div className="flex items-center justify-between gap-2 mt-2">
        <span className="text-xs text-zinc-600">{design.label} · {design.w}×{design.h}</span>
        <button type="button" disabled={!ready} onClick={download} className="chip text-xs inline-flex items-center gap-1 disabled:opacity-50" data-testid={`kit-image-download-${design.id}`}><Download size={12}/> PNG</button>
      </div>
    </div>
  );
};

const KitPost = ({ post, code }) => {
  const copy = () => navigator.clipboard.writeText(post.text).then(() => toast.success("Post copied - paste it into LinkedIn"));
  return (
    <div className="card-warm p-4 flex flex-col" data-testid={`kit-post-${post.id}`}>
      <div className="font-semibold text-sm">{post.title}</div>
      <pre className="whitespace-pre-wrap font-sans-ui text-xs text-zinc-700 mt-2 max-h-48 overflow-y-auto flex-1" data-testid={`kit-post-text-${post.id}`}>{post.text}</pre>
      <div className="flex gap-2 mt-3">
        <button type="button" onClick={copy} className="chip text-xs inline-flex items-center gap-1" data-testid={`kit-post-copy-${post.id}`}><Copy size={12}/> Copy post</button>
        <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(`${process.env.REACT_APP_BACKEND_URL}/api/share/r/${code}`)}`} target="_blank" rel="noopener noreferrer" onClick={copy} className="chip text-xs inline-flex items-center gap-1" data-testid={`kit-post-linkedin-${post.id}`}><Linkedin size={12}/> Copy &amp; open LinkedIn</a>
      </div>
    </div>
  );
};

export const MarketingKit = ({ ambassador }) => {
  const amb = { code: ambassador.code, referral_link: ambassador.referral_link, name: ambassador.name };
  const posts = KIT_POSTS(amb, ambassador.name.split(" ")[0]);
  return (
    <section data-testid="ambassador-marketing-kit">
      <h2 className="font-display text-2xl">Marketing kit</h2>
      <p className="text-sm text-zinc-600 mt-1 mb-4">Ready-made LinkedIn posts and images with your code <b>{ambassador.code}</b> and link built in. Edit the wording if you like, but keep claims accurate and keep the commission disclosure.</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 items-start">{KIT_IMAGES.map((d) => <KitImage key={d.id} design={d} amb={amb}/>)}</div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">{posts.map((p) => <KitPost key={p.id} post={p} code={ambassador.code}/>)}</div>
    </section>
  );
};
